/**
 * Google Identity Services loader (ENG-6165).
 *
 * Injects Google's script once per page and resolves with `google.accounts.id`.
 * Concurrent callers share one load; a failed load removes its script and is
 * forgotten, so the next call tries again. Only the slice of the library the
 * "Continue with Google" button uses is typed here.
 */
const GSI_SRC = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  /** The Google ID token (a JWT). A credential: never log it. */
  credential: string;
}

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  ux_mode?: 'popup' | 'redirect';
  context?: 'signin' | 'signup' | 'use';
  use_fedcm_for_button?: boolean;
}

export interface GoogleButtonConfiguration {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  /** Button language; Google otherwise follows the browser's. */
  locale?: string;
}

export interface GoogleId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, options: GoogleButtonConfiguration): void;
}

let loading: Promise<GoogleId> | null = null;

function loadedLibrary(): GoogleId | undefined {
  return (window as Window & { google?: { accounts?: { id?: GoogleId } } }).google?.accounts?.id;
}

function injectScript(): Promise<GoogleId> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => {
      script.remove();
      loading = null;
      reject(new Error('Google Identity Services failed to load'));
    };
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => {
      const library = loadedLibrary();
      if (library) resolve(library);
      else fail();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });
}

export function loadGoogleIdentity(): Promise<GoogleId> {
  if (!loading) {
    const library = loadedLibrary();
    loading = library ? Promise.resolve(library) : injectScript();
  }
  return loading;
}

/** Test seam: forget the load, as a fresh page would. */
export function resetGoogleIdentityForTests(): void {
  loading = null;
}
